import React, { Dispatch, SetStateAction, useState } from 'react';
import { IGetProject } from '../../interfaces/project.interface';
import { Card, Dropdown, InputGroup, Spinner } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
    useDeleteProjectMutation,
    useDuplicateProjectMutation,
    useLazyGetProjectByIdQuery,
    useRenameProjectMutation,
} from '../../store/api/editor.api';
import { displayErrorToast, displaySuccessToast } from '../../utils/ToastUtils';
import { toast } from 'react-toastify';
import DeleteModal from './ProjectDelete';
import { getTemplateFromProject } from '../../store/selector/editor.selector';
import { SetTemplateLoadingState } from '../../store/slice/editor.slice';
import MarkAsTemplateModal from './MarkAsTemplateModal';
import { getActiveBrand } from '../../store/selector/brand.selector';

interface IProps {
    store: any;
    project: IGetProject;
    projects: IGetProject[];
    index: number;
    projectsFetchLoading: boolean;
    setCurrentPage: Dispatch<SetStateAction<number>>;
    handleProjectClick: (project: IGetProject) => void;
}

const ProjectCard = ({
    store,
    project,
    projects,
    index,
    projectsFetchLoading,
    setCurrentPage,
    handleProjectClick,
}: IProps) => {
    const dispatch = useAppDispatch();
    const activeBrand = useAppSelector(getActiveBrand);
    const templateFromProject = useAppSelector(getTemplateFromProject);
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [saveImageLoading, setSaveImageLoading] = useState(false);
    const [duplicateProjectLoading, setDuplicateProjectLoading] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showMarkAsTemplate, setShowMarkAsTemplate] = useState(false);
    const [getProjectById] = useLazyGetProjectByIdQuery();
    const [renameProject] = useRenameProjectMutation();
    const [duplicateProject] = useDuplicateProjectMutation();
    const [deleteProject, { isLoading: deleteLoading }] = useDeleteProjectMutation();

    const handleSaveToPC = async (name: string, id: number) => {
        const res = await getProjectById(id);
        const jsonStr = JSON.stringify(res.data.json, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const fileName = name;
        if (fileName) {
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();

            URL.revokeObjectURL(url);
        }
    };

    const handleSaveasImage = async (name: string, id: number) => {
        try {
            setSaveImageLoading(true);
            const res = await getProjectById(id);
            const previewUrl = res.data.preview;
            if (previewUrl) {
                const response = await fetch(previewUrl);
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);

                const a = document.createElement('a');
                a.href = url;
                a.download = name;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                setSaveImageLoading(false);
            }
        } catch (error) {
            setSaveImageLoading(false);
            displayErrorToast('Error saving project. Please try again');
        }
    };

    const handleRenameProject = async (currentName: string, id: number) => {
        const newName = window.prompt('New project name', currentName);
        if (newName) {
            const res: any = await renameProject({
                id: id,
                data: {
                    name: newName,
                },
            });
            if (res.error) {
                displayErrorToast('There was an error saving name');
            } else {
                displaySuccessToast('Successfully changed name');
            }
        }
    };

    const handleDupilcateClick = async (nameTag: string, projectId: number) => {
        if (!activeBrand) return;
        // setDuplicateProjectLoading(true);
        const payload = {
            id: projectId,
            data: {
                projectName: nameTag + '(copy)',
                brandId: activeBrand.id,
            },
        };
        try {
            await duplicateProject(payload).then((async) => {
                setCurrentPage((prev) => {
                    switch (prev) {
                        case 0:
                            return 1;
                        case 1:
                            return 0;
                        default:
                            return 1;
                    }
                });
            });
        } catch (error) {
            toast('Error while duplicating the project, Please Try Again', {
                type: 'warning',
            });
        }
    };

    const onDeleteClick = (projectId: number) => {
        setShowDelete(true);
    };

    const onMarkAsTemplateClick = () => {
        setShowMarkAsTemplate(true);
    };

    const handleDeleteClick = async () => {
        const idToDelete = project.id;
        const currentlyOpenedInEditor =
            templateFromProject && templateFromProject.id === idToDelete;

        if (currentlyOpenedInEditor) dispatch(SetTemplateLoadingState(true));

        try {
            await deleteProject(idToDelete).then(async () => {
                setCurrentPage((prev) => {
                    switch (prev) {
                        case 0:
                            return 1;
                        case 1:
                            return 0;
                        default:
                            return 1;
                    }
                });

                setShowDelete(false);
                if (currentlyOpenedInEditor) {
                    store.clear();
                    dispatch(SetTemplateLoadingState(false));
                }
                // let openProject = null;
                // if (projects.length) {
                //     for (let i = 0; i < projects.length; i++) {
                //         if (projects[i].id === idToDelete) {
                //             continue;
                //         } else {
                //             openProject = projects[i].id;
                //             break;
                //         }
                //     }
                // }
                // if (openProject) {
                //     const res = await getProjectById(openProject);

                //     dispatch(updateProject({ id: res.data.id, name: res.data.name }));
                //     store.loadJSON(res.data.json);
                // } else {
                //     store.clear();
                // }
            });
        } catch (error) {
            setShowDelete(false);
            if (currentlyOpenedInEditor) dispatch(SetTemplateLoadingState(false));
        }
    };

    return (
        <Card className="project-content box-shadow p-2">
            <Card.Img
                onLoad={() => setIsImageLoaded(true)}
                onClick={() => {
                    handleProjectClick(project);
                    const tempInd = index / 15;
                    setCurrentPage(parseInt(tempInd.toString()) + 1);
                }}
                variant="top"
                src={project.preview}
                alt={`${project.name}`}
                style={{
                    width: '100%',
                }}
            />
            <Card.Title
                className="p-2 mb-0"
                style={{
                    width: '100%',
                }}
            >
                <h3 className="text-ellipsis mb-0">{project.name}</h3>
            </Card.Title>
            <div className="project-options">
                <InputGroup className="custom-dropdown">
                    <Dropdown autoClose="outside">
                        <Dropdown.Toggle
                            variant="outline-primary"
                            className="dropdown__toggle iconbtn"
                            id="dropdown-autoclose-inside"
                        >
                            <i className="ph-fill ph-dots-three-outline "></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="dropdown__toggle--menu">
                            <Dropdown.Item
                                onClick={() => {
                                    if (
                                        templateFromProject === null ||
                                        templateFromProject.id !== project.id
                                    ) {
                                        handleProjectClick(project);
                                    }
                                }}
                            >
                                <i className="ph ph-arrow-square-out lg"></i>
                                Open
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleSaveToPC(project.name, project.id)}>
                                <i className="ph ph-archive-tray lg"></i>
                                Save to PC
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => handleSaveasImage(project.name, project.id)}
                            >
                                <i className="ph ph-archive-tray lg"></i>
                                Save as Image
                                {saveImageLoading && <Spinner />}
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => handleRenameProject(project.name, project.id)}
                            >
                                <i className="ph  ph-pencil-simple-line lg"></i>
                                Rename
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => handleDupilcateClick(project.name, project.id)}
                            >
                                <i className="ph ph-copy lg"></i> Duplicate
                                {duplicateProjectLoading && <Spinner />}
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => onMarkAsTemplateClick()}
                                disabled={projectsFetchLoading}
                            >
                                <i className="ph ph-article lg"></i>
                                Mark as Template
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => onDeleteClick(project.id)}
                                disabled={projectsFetchLoading}
                            >
                                <i className="pxi-trash lg"></i>
                                Delete
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </InputGroup>
            </div>

            <DeleteModal
                onDelete={handleDeleteClick}
                loading={deleteLoading}
                onHide={() => {
                    setShowDelete(false);
                }}
                show={showDelete}
            />
            <MarkAsTemplateModal
                show={showMarkAsTemplate}
                onHide={() => setShowMarkAsTemplate(false)}
                projectId={project.id}
            />
        </Card>
    );
};

export default ProjectCard;
